import { useOrgRoleSurfaces } from "@/hooks/useOrgRoleSurfaces";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientOrdersPage } from "@/features/client-orders";
import OrdersList from "./OrdersList";

/**
 * BLU-660 — component-level switch for the `/orders` route.
 *
 * A pure 3PL client (a Shopify-store org that owns no location and only fulfils
 * through a junction at another org's shared location) has no Purchase Orders
 * workflow — the read-only client order dashboard is their only fulfilment
 * window. So for `isPureClient` we render `ClientOrdersPage`; every owner /
 * hybrid org sees the unchanged `OrdersList` (Purchase Orders).
 *
 * `isPureClient` stays false while the `getOrgRoleSurfaces` query is loading,
 * disabled (shared-locations flag off), or erroring — the established BLU-658
 * contract (never flash-hide / redirect on an unconfirmed answer). The ONLY
 * ambiguous window is `isLoading`: render a neutral skeleton then, so we never
 * flash the wrong surface before the answer resolves. In practice AppSidebar
 * already mounts `useOrgRoleSurfaces`, so this query is usually cache-warm by
 * the time `/orders` renders and the skeleton is skipped.
 *
 * Note: React Query v5 reports `isLoading = false` for a DISABLED query (the
 * shared-locations flag being off), so single-tenant orgs fall straight through
 * to `OrdersList` with no skeleton — byte-equivalent to before this switch.
 */
export default function OrdersRouteSwitch() {
  const { isPureClient, isLoading } = useOrgRoleSurfaces();

  if (isPureClient) {
    return <ClientOrdersPage />;
  }

  if (isLoading) {
    return <OrdersRouteSkeleton />;
  }

  return <OrdersList />;
}

/** Neutral placeholder shown only during the brief `getOrgRoleSurfaces`
 *  resolving window — deliberately generic so it resembles neither the PO list
 *  nor the client dashboard. */
function OrdersRouteSkeleton() {
  return (
    <div className="flex flex-col">
      <main className="w-full flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 pb-2 pt-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-8 w-40" />
        </div>
        <div className="mt-6 space-y-2 rounded-lg border bg-card p-4 shadow">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      </main>
    </div>
  );
}
